import {
    type FormEvent,
    type KeyboardEventHandler,
    type Ref,
    ReactNode,
    forwardRef,
    useState,
} from "react";
import {
    Autocomplete,
    Box,
    Button,
    ButtonProps,
    Dropdown,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    Sheet,
    Stack,
    Tooltip,
    Typography,
} from "@mui/joy";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { Popper } from "@mui/base/Popper";
import ChevronLeftOutlined from "@mui/icons-material/ChevronLeftOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import FilterListOutlined from "@mui/icons-material/FilterListOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import type { CollectionFilterState } from "../../../hooks/useCollectionView";
import { Keycap } from "../../../shell/primitives/Keycap";

export interface View {
    key: string;
    label: string;
    /** A number, or "—" while it is being counted. */
    count?: number | string;
}

/**
 * Views: one segmented control for the exclusive slices of a list (status buckets).
 * A group of toggle buttons, never tabs; the header never carries tabs. The chosen
 * view is tinted in the brand colour so it is unmistakable next to filter controls.
 */
export function ViewSwitch({
    views,
    value,
    onChange,
    ariaLabel = "View",
}: {
    views: View[];
    value: string;
    onChange: (key: string) => void;
    ariaLabel?: string;
}) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            role="group"
            aria-label={ariaLabel}
            sx={{ p: 0.375, borderRadius: "md", bgcolor: "background.level1", flexShrink: 0 }}
        >
            {views.map((view) => {
                const on = view.key === value;
                return (
                    <Box
                        key={view.key}
                        component="button"
                        type="button"
                        aria-pressed={on}
                        onClick={() => onChange(view.key)}
                        sx={{
                            all: "unset",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            height: 28,
                            borderRadius: "sm",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            color: on ? "primary.softColor" : "text.secondary",
                            bgcolor: on ? "primary.softBg" : "transparent",
                            "&:hover": { color: on ? "primary.softColor" : "text.primary" },
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "focusVisible",
                                outlineOffset: 1,
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "var(--type-secondary)",
                                fontWeight: on ? 600 : 500,
                                color: "inherit",
                                lineHeight: 1,
                            }}
                        >
                            {view.label}
                        </Typography>
                        {view.count !== undefined && (
                            <Typography
                                sx={{
                                    fontSize: "var(--type-caption)",
                                    fontWeight: 500,
                                    color: on ? "primary.softColor" : "text.tertiary",
                                    fontVariantNumeric: "tabular-nums",
                                    lineHeight: 1,
                                }}
                            >
                                {view.count}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
}

/** An applied filter, removable. Reads "Discipline Plumbing ×". */
export function AppliedFilter({
    label,
    value,
    onRemove,
}: {
    label: string;
    value: string;
    onRemove: () => void;
}) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
                height: 28,
                pl: 1.25,
                pr: 0.5,
                borderRadius: "sm",
                border: "1px solid",
                borderColor: "divider",
                whiteSpace: "nowrap",
                flexShrink: 0,
            }}
        >
            <Typography sx={{ fontSize: "var(--type-caption)", color: "text.tertiary" }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: "var(--type-caption)", fontWeight: 600 }}>
                {value}
            </Typography>
            <Box
                component="button"
                type="button"
                aria-label={`Remove ${label} filter`}
                onClick={onRemove}
                sx={{
                    all: "unset",
                    cursor: "pointer",
                    color: "text.tertiary",
                    px: 0.5,
                    fontSize: "var(--type-body)",
                    lineHeight: 1,
                    "&:hover": { color: "text.primary" },
                }}
            >
                ×
            </Box>
        </Stack>
    );
}

export interface QuietProps extends Omit<
    ButtonProps,
    "startDecorator" | "endDecorator" | "variant" | "color" | "size"
> {
    /** The glyph; a quiet control may be a label only when it carries state. */
    icon?: ReactNode;
    /** Shown as a badge; the control goes dark while it is non-zero. */
    count?: number;
    /** Tooltip for a glyph-only control. */
    title?: string;
    /** Where a quiet link goes (with `component="a"`): opening a page changes no data. */
    href?: string;
    target?: string;
    rel?: string;
}

/**
 * Quiet control: adjusts the view and changes no data. Plain text or glyph, no
 * chrome, hover tint only. A label only where the control carries state.
 * A real Joy Button underneath, so it can be a MenuButton root.
 */
export const Quiet = forwardRef<HTMLButtonElement, QuietProps>(function Quiet(
    { icon, count, title, children, sx, ...rest },
    ref,
) {
    const active = !!count;
    const button = (
        <Button
            ref={ref}
            variant="plain"
            color="neutral"
            data-level="quiet"
            startDecorator={icon}
            endDecorator={
                active ? (
                    <Box
                        sx={{
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: 9,
                            bgcolor: "text.primary",
                            color: "background.surface",
                            fontSize: "var(--type-caption)",
                            fontWeight: 600,
                            display: "grid",
                            placeItems: "center",
                        }}
                        data-testid="quiet-count"
                    >
                        {count}
                    </Box>
                ) : undefined
            }
            {...rest}
            sx={[
                {
                    minHeight: 30,
                    px: children ? 1 : 0.75,
                    fontSize: "var(--type-secondary)",
                    fontWeight: active ? 600 : 500,
                    color: active ? "text.primary" : "text.secondary",
                    "--Button-gap": "6px",
                    "& svg": { fontSize: 18 },
                    "&:hover": { bgcolor: "background.level1", color: "text.primary" },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {children}
        </Button>
    );
    return children || !title ? (
        button
    ) : (
        <Tooltip title={title} variant="soft">
            {button}
        </Tooltip>
    );
});

/**
 * A step back within what is open (DESIGN.md · Navigation): a chevron and where it goes, "‹ All
 * pages", "‹ SUNDT-RFI-260", "‹ Back to the form". It moves inside what is open, not through the
 * tab's history, so the desktop draws it too. `name` is what assistive tech hears when the label alone does not say
 * that it goes back ("Back to All pages").
 */
export function WayBack({
    label,
    name,
    onClick,
}: {
    label: ReactNode;
    name?: string;
    onClick: () => void;
}) {
    return (
        <Quiet icon={<ChevronLeftOutlined />} aria-label={name} onClick={onClick}>
            {label}
        </Quiet>
    );
}

/** A page's own filter and quiet tools at the right end of the header's last row: a soft search
 *  field, then the tools, on one line beside the view switches and the actions. */
export function ControlTools({
    search,
    onSearch,
    placeholder,
    children,
}: {
    search?: string;
    onSearch?: (value: string) => void;
    placeholder?: string;
    children?: ReactNode;
}) {
    return (
        <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            alignItems="center"
            data-testid="control-tools"
            sx={{ flexWrap: "nowrap", justifyContent: "flex-end", minWidth: 0 }}
        >
            {onSearch && (
                <SearchField
                    value={search ?? ""}
                    onChange={onSearch}
                    placeholder={placeholder}
                    width="clamp(160px, 18vw, 240px)"
                />
            )}
            {children}
        </Stack>
    );
}

/** An info glyph that says what a search covers; focusable so a keyboard reader gets it too. */
export function SearchInfo({ text }: { text: string }) {
    return (
        <Tooltip title={text} variant="soft">
            <Box
                component="span"
                tabIndex={0}
                data-testid="search-info"
                sx={{ display: "inline-flex", color: "text.tertiary", mr: 0.5 }}
            >
                <InfoOutlined sx={{ fontSize: 18 }} />
            </Box>
        </Tooltip>
    );
}

export function SearchField({
    value,
    onChange,
    placeholder,
    width = 240,
    label,
    info,
    inputRef,
    onKeyDown,
    keycap,
    type,
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** The accessible name when the placeholder is shorter ("Search RFIs"). */
    label?: string;
    /** What the search covers: an info glyph with this text in a tooltip. */
    info?: string;
    /** The inner input's ref and key handler, from `useSearchKeys` (Mod+F focuses, Escape clears). */
    inputRef?: Ref<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    /** A shortcut hint drawn at the field's end ("Mod+F"). */
    keycap?: string;
    /** "search" gives the input the searchbox role (the hosted register header). */
    type?: "search";
    /** px, or a length the field fills ("100%" of a box that sizes it). */
    width?: number | string;
}) {
    return (
        <Input
            type={type}
            aria-label={label ?? placeholder ?? "Search"}
            placeholder={placeholder ?? "Search"}
            startDecorator={<SearchOutlined sx={{ fontSize: 18, color: "text.tertiary" }} />}
            endDecorator={
                info || keycap ? (
                    <>
                        {info ? <SearchInfo text={info} /> : null}
                        {keycap ? <Keycap spec={keycap} /> : null}
                    </>
                ) : undefined
            }
            slotProps={{
                input: {
                    ref: inputRef,
                    onKeyDown,
                    autoComplete: "off",
                    spellCheck: false,
                },
            }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            // The theme's field: flat, its tone inside a border.
            sx={{
                width,
                mr: 1,
                "--Input-focusedThickness": "1px",
                boxShadow: "none",
            }}
        />
    );
}

/** A filter chip's button: small, outlined, its name quiet and its value after it. A fixed
 *  filter that is stated, not chosen (Usage's audience), draws the same chip with no menu. */
export const filterChipSx = {
    minHeight: 28,
    height: 28,
    px: 1.25,
    borderRadius: "sm",
    borderColor: "divider",
    fontSize: "var(--type-caption)",
    fontWeight: 400,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "--Button-gap": "4px",
    "& svg": { fontSize: 16, color: "text.tertiary" },
} as const;

/** "Priority:" then its value, strong once one is chosen. */
export function FilterChipText({
    label,
    value,
    chosen,
}: {
    label: string;
    value: string;
    chosen: boolean;
}) {
    return (
        <>
            <Typography component="span" sx={{ fontSize: "inherit", color: "text.tertiary" }}>
                {label}:
            </Typography>
            <Typography
                component="span"
                sx={{
                    fontSize: "inherit",
                    ml: 0.5,
                    fontWeight: chosen ? 600 : 400,
                    color: chosen ? "text.primary" : "text.secondary",
                }}
            >
                {value}
            </Typography>
        </>
    );
}

/**
 * A filter that stays on show, as a chip: "Priority: any" until a value is chosen, then
 * "Priority: High". It opens the filter's choices, its "Any" option first, which clears it.
 * A register draws one for each workflow field it shows as a column; its other filters stay
 * in the Filter tool and read back as `AppliedFilters` chips. A field with no fixed list of
 * choices takes a typed value instead (`FilterValueChip`).
 */
export function FilterChip({
    filter,
    label,
    anyLabel = "any",
}: {
    filter: CollectionFilterState;
    label: string;
    /** What the chip reads while no value is chosen: "Status: any" by default. */
    anyLabel?: string;
}) {
    const chosen =
        filter.value == null ? undefined : filter.options.find((o) => o.value === filter.value);
    const value = chosen?.label ?? filter.value ?? anyLabel;
    return (
        <Dropdown>
            <MenuButton
                variant="outlined"
                color="neutral"
                aria-label={`${label}: ${value}`}
                endDecorator={<ExpandMoreOutlined />}
                data-kit="filter-chip"
                sx={filterChipSx}
            >
                <FilterChipText label={label} value={value} chosen={!!chosen} />
            </MenuButton>
            <Menu placement="bottom-start" sx={{ maxHeight: 320, overflow: "auto", zIndex: 1300 }}>
                {filter.options.map((option) => (
                    <MenuItem
                        key={option.value ?? ""}
                        selected={option.value === filter.value}
                        onClick={() => filter.setValue(option.value)}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </Dropdown>
    );
}

/**
 * A filter chip for a value typed rather than picked (a free-text, number or date workflow
 * field): "Floor: any" until a value is applied, then "Floor: 3". It opens a small panel with
 * the value's input; Apply (or Enter) filters, Clear takes the filter away. `parse` turns what
 * was typed into the filter's value, or null while it is not one.
 */
export function FilterValueChip({
    label,
    value,
    display,
    type,
    placeholder,
    hint,
    parse,
    onChange,
}: {
    label: string;
    /** The applied value, null for none. */
    value: string | null;
    /** The applied value in words ("Oct 2", "1,234"); the value itself otherwise. */
    display?: string | null;
    type: "text" | "number" | "date";
    placeholder?: string;
    /** A line under the input saying how the value matches. */
    hint?: string;
    parse: (typed: string) => string | null;
    onChange: (value: string | null) => void;
}) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [typed, setTyped] = useState("");
    const open = !!anchor;
    const close = () => setAnchor(null);
    const parsed = parse(typed);
    const shown = value == null ? "any" : (display ?? value);
    const apply = () => {
        if (parsed == null) return;
        onChange(parsed);
        close();
    };
    return (
        <>
            <Button
                variant="outlined"
                color="neutral"
                aria-label={`${label}: ${shown}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                endDecorator={<ExpandMoreOutlined />}
                data-kit="filter-chip"
                sx={filterChipSx}
                onClick={(e) => {
                    setTyped(value ?? "");
                    setAnchor(open ? null : e.currentTarget);
                }}
            >
                <FilterChipText label={label} value={shown} chosen={value != null} />
            </Button>
            <Popper open={open} anchorEl={anchor} placement="bottom-start" style={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={close}>
                    <Sheet
                        variant="outlined"
                        role="dialog"
                        aria-label={label}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") close();
                        }}
                        sx={{
                            width: 260,
                            maxWidth: "calc(100vw - 32px)",
                            mt: 0.5,
                            p: 1.5,
                            borderRadius: "md",
                            boxShadow: "md",
                            bgcolor: "background.surface",
                        }}
                    >
                        <Stack
                            component="form"
                            spacing={1}
                            onSubmit={(e: FormEvent) => {
                                e.preventDefault();
                                apply();
                            }}
                        >
                            <FormControl>
                                <FormLabel>{label}</FormLabel>
                                <Input
                                    autoFocus
                                    type={type}
                                    value={typed}
                                    placeholder={placeholder}
                                    onChange={(e) => setTyped(e.target.value)}
                                    slotProps={{ input: { maxLength: 200 } }}
                                />
                                {hint && <FormHelperText>{hint}</FormHelperText>}
                            </FormControl>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="flex-end"
                            >
                                {value != null && (
                                    <Quiet
                                        onClick={() => {
                                            onChange(null);
                                            close();
                                        }}
                                    >
                                        Clear
                                    </Quiet>
                                )}
                                <Button type="submit" disabled={parsed == null}>
                                    Apply
                                </Button>
                            </Stack>
                        </Stack>
                    </Sheet>
                </ClickAwayListener>
            </Popper>
        </>
    );
}

/** The applied filters of a `useCollectionView`, as removable chips. Filters at their "All" value are not shown. */
export function AppliedFilters({
    filters,
    labels,
}: {
    filters: CollectionFilterState[];
    labels?: Record<string, string>;
}) {
    const applied = filters.filter((f) => f.value != null);
    if (!applied.length) return null;
    return (
        <>
            {applied.map((f) => {
                const option = f.options.find((o) => o.value === f.value);
                return (
                    <AppliedFilter
                        key={f.key}
                        label={labels?.[f.key] ?? humanize(f.key)}
                        value={option?.label ?? String(f.value)}
                        onRemove={() => f.setValue(null)}
                    />
                );
            })}
        </>
    );
}

/** A `useCollectionView` select filter as the view switcher: its "All" option first, then each value, with optional counts. */
export function FilterViewSwitch({
    filter,
    counts,
    ariaLabel,
}: {
    filter: CollectionFilterState;
    counts?: Record<string, number>;
    ariaLabel?: string;
}) {
    const views: View[] = filter.options.map((o) => ({
        key: o.value ?? ALL,
        label: o.label,
        count: counts?.[o.value ?? ALL],
    }));
    return (
        <ViewSwitch
            views={views}
            value={filter.value ?? ALL}
            onChange={(k) => filter.setValue(k === ALL ? null : k)}
            ariaLabel={ariaLabel ?? humanize(filter.key)}
        />
    );
}
/**
 * The Filter quiet tool. Opens a panel with one searchable field per filter, so a
 * register with hundreds of names stays usable; the applied count is the badge.
 * Applied values show as `AppliedFilters` chips beside the views.
 */
export function FilterMenu({
    filters,
    labels,
    renderFilter,
    onClearAll,
}: {
    filters: CollectionFilterState[];
    labels?: Record<string, string>;
    /** Server-filtered registers can supply text/date fields without page-local option lists. */
    renderFilter?: (filter: CollectionFilterState) => ReactNode;
    /** Clear URL filters in one transaction when the caller owns their shared state. */
    onClearAll?: () => void;
}) {
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const open = !!anchor;
    const active = filters.filter((f) => f.value != null).length;
    const close = () => setAnchor(null);
    return (
        <>
            <Quiet
                icon={<FilterListOutlined />}
                count={active}
                aria-label="Filter"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={(e) => setAnchor(open ? null : e.currentTarget)}
            >
                Filter
            </Quiet>
            <Popper open={open} anchorEl={anchor} placement="bottom-end" style={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={close}>
                    <Sheet
                        variant="outlined"
                        role="dialog"
                        aria-label="Filters"
                        onKeyDown={(e) => {
                            if (e.key === "Escape") close();
                        }}
                        sx={{
                            width: 320,
                            maxWidth: "calc(100vw - 32px)",
                            mt: 0.5,
                            p: 2,
                            borderRadius: "md",
                            boxShadow: "md",
                            bgcolor: "background.surface",
                        }}
                    >
                        <Stack spacing={1.5}>
                            {filters.map((filter) => {
                                const custom = renderFilter?.(filter);
                                if (custom != null) return <Box key={filter.key}>{custom}</Box>;
                                const label = labels?.[filter.key] ?? humanize(filter.key);
                                const options = filter.options.filter(
                                    (o): o is { value: string; label: string } => o.value != null,
                                );
                                const everything =
                                    filter.options.find((o) => o.value == null)?.label ?? "All";
                                return (
                                    <FormControl key={filter.key}>
                                        <FormLabel
                                            sx={{
                                                fontSize: "var(--type-caption)",
                                                color: "text.tertiary",
                                            }}
                                        >
                                            {label}
                                        </FormLabel>
                                        <Autocomplete
                                            options={options}
                                            getOptionLabel={(o) => o.label}
                                            isOptionEqualToValue={(a, b) => a.value === b.value}
                                            value={
                                                options.find((o) => o.value === filter.value) ??
                                                null
                                            }
                                            onChange={(_, option) =>
                                                filter.setValue(option?.value ?? null)
                                            }
                                            placeholder={everything}
                                            slotProps={{
                                                input: { "aria-label": label },
                                                // Inside the panel, so choosing an option is not a click away.
                                                listbox: { disablePortal: true },
                                            }}
                                        />
                                    </FormControl>
                                );
                            })}
                            {active > 0 && (
                                <Box>
                                    <Quiet
                                        onClick={
                                            onClearAll ??
                                            (() => filters.forEach((f) => f.setValue(null)))
                                        }
                                    >
                                        Clear all
                                    </Quiet>
                                </Box>
                            )}
                        </Stack>
                    </Sheet>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
const ALL = "__all__";
const humanize = (key: string) =>
    key
        .replace(/[_-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (c) => c.toUpperCase());
